import {
  type CatalogAuthority,
  canUseCatalogAction,
} from '../api/formal/phase1Permissions';

function scopedRead(
  user: CatalogAuthority,
  code: string,
  resource: string,
  fine = false,
): boolean {
  if (!user.permissions?.includes(code)) return false;
  const scopes =
    user.permissionDataScopes === undefined
      ? user.dataScopes
      : user.permissionDataScopes[code];
  return (
    scopes?.some(
      (scope) =>
        scope.resourceType === resource &&
        (fine
          ? ['all', 'self'].includes(scope.scopeType)
          : scope.scopeType !== 'none'),
    ) ?? false
  );
}

/** Admit schema metadata for configuration actions or the caller's own business module; never grant business records. */
export function canReadFieldMetadata(
  user: CatalogAuthority | undefined,
  group?: 'device' | 'package' | 'plan' | 'customer',
  kind: 'definitions' | 'display-names' = 'definitions',
): boolean {
  if (!user) return false;
  if (
    user.permissions?.some((code) =>
      [
        'settings.read',
        'quote.field_definition.manage',
        'quote.field_preset.manage',
        'quote.field_pool.manage',
        'quote.field_combination.manage',
        'quote.field_formula.manage',
        ...(kind === 'definitions' ? ['settings.output_options.manage'] : []),
      ].includes(code),
    )
  )
    return true;
  const legacyCatalog =
    user.permissionDataScopes === undefined &&
    (scopedRead(user, 'catalog.read', 'catalog') ||
      scopedRead(user, 'solution.read', 'catalog'));
  const quote = scopedRead(user, 'quote.read', 'quote');
  const customer = scopedRead(user, 'customer.read', 'customer');
  if (group === 'customer') return customer;
  const moduleRead = (module: 'equipment' | 'package' | 'plan') =>
    canUseCatalogAction(user, module, 'read') &&
    scopedRead(user, `${module}.read`, module, true);
  if (group)
    return (
      quote ||
      legacyCatalog ||
      moduleRead(group === 'device' ? 'equipment' : group)
    );
  return (
    quote ||
    customer ||
    legacyCatalog ||
    ['equipment', 'package', 'plan'].some((module) =>
      moduleRead(module as 'equipment' | 'package' | 'plan'),
    )
  );
}
